import React, { useState } from 'react';
import { Calendar, User, Phone, Mail, MessageSquare, Clock, CheckCircle } from 'lucide-react';
import { sendAppointmentEmail } from '../utils/emailService';

const Appointment: React.FC = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    department: '',
    preferredDate: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const departments = [
    'General OPD',
    'Paediatrics',
    'Physician',
    'Cardiology',
    'O/Gynaecology',
    'General Surgery',
    'Orthopaedics',
    'ENT',
    'Dental',
    'Optician',
    'Psychiatric',
    'Neurology',
    'Neurosurgery',
    'Physiotherapy',
    'Clinical Psychology',
    'Urology',
    'Gastro Enterology',
    'Diabetic',
    'Dermatology',
    'Anaesthesiologist and Critical Care',
    'Ophthalmology',
    'Haematology',
    'Paediatric Surgery',
    'Pathology',
    'Respiratory and Physician',
    'Physician and Nephrology',
    'Oncology',
    'Optometrist',
    'Occupational Therapy',
    'Emergency Specialist',
    'Radiologist',
    'TB and CTC Clinic',
    'Cardiothoracic and Vascular Surgery',
    'General and Paediatric Surgeon'
  ];


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await sendAppointmentEmail({
        fullName: formData.fullName,
        phone: formData.phone,
        email: formData.email,
        department: formData.department,
        preferredDate: formData.preferredDate,
        message: formData.message
      });

      setIsSubmitted(true);
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({
          fullName: '',
          phone: '',
          email: '',
          department: '',
          preferredDate: '',
          message: ''
        });
      }, 5000);
    } catch (error: any) {
      console.error('Error submitting appointment:', error);
      setIsSubmitting(false);

      if (error.message === 'EMAIL_NOT_CONFIGURED') {
        alert('Email service is not yet configured. Please call us directly at +255 757 685 894 to book your appointment. We apologize for the inconvenience.');
      } else {
        alert('Failed to send appointment request. Please try again or call us at +255 757 685 894.');
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center section-padding">
        <div className="container-max">
          <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="text-6xl mb-6">✅</div>
            <h2 className="text-2xl font-bold text-green-600 mb-4">Appointment Booked Successfully!</h2>
            <p className="text-gray-600 mb-6">
              Thank you for booking your appointment with Rabininsia Memorial Hospital. 
              You will receive a confirmation via SMS or email shortly.
            </p>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="font-medium text-green-800 mb-2">What's Next?</h3>
              <ul className="text-sm text-green-700 space-y-1">
                <li>• You'll receive a confirmation message</li>
                <li>• Our staff will contact you to confirm the appointment</li>
                <li>• Please arrive 15 minutes before your scheduled time</li>
                <li>• Bring your ID and insurance card</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-8 md:py-12 lg:py-16 px-4 sm:px-6 lg:px-8">
        <div className="container-max">
          <div className="text-center">
            <h1 className="text-2xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 md:mb-6">Book an Appointment</h1>
            <p className="text-base md:text-lg lg:text-xl text-blue-100 max-w-3xl mx-auto px-4">
              Schedule your visit with our experienced medical professionals. 
              We're here to provide you with the best healthcare services.
            </p>
          </div>
        </div>
      </section>

      {/* Appointment Form */}
      <section className="py-8 md:py-12 lg:py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="container-max">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
              {/* Form */}
              <div className="lg:col-span-2">
                <div className="bg-white rounded-lg shadow-lg p-4 md:p-6 lg:p-8">
                  <div className="flex items-center mb-6">
                    <Calendar className="h-6 w-6 md:h-8 md:w-8 text-blue-600 mr-3" />
                    <h2 className="text-xl md:text-2xl font-bold text-gray-900">Appointment Details</h2>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
                    {/* Personal Information */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Full Name *
                        </label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 md:h-5 md:w-5" />
                          <input
                            type="text"
                            name="fullName"
                            required
                            value={formData.fullName}
                            onChange={handleChange}
                            className="w-full pl-8 md:pl-10 pr-4 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm md:text-base"
                            placeholder="Enter your full name"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Phone Number *
                        </label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 md:h-5 md:w-5" />
                          <input
                            type="tel"
                            name="phone"
                            required
                            value={formData.phone}
                            onChange={handleChange}
                            className="w-full pl-8 md:pl-10 pr-4 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm md:text-base"
                            placeholder="+255 7XX XXX XXX"
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 md:h-5 md:w-5" />
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className="w-full pl-8 md:pl-10 pr-4 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm md:text-base"
                          placeholder="your.email@example.com"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Clinic *
                      </label>
                      <select
                        name="department"
                        required
                        value={formData.department}
                        onChange={handleChange}
                        className="w-full px-4 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm md:text-base"
                      >
                        <option value="">Select Clinic</option>
                        {departments.map((dept) => (
                          <option key={dept} value={dept}>{dept}</option>
                        ))}
                      </select>
                    </div>

                    {/* Date and Time */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Preferred Date *
                      </label>
                      <input
                        type="date"
                        name="preferredDate"
                        required
                        value={formData.preferredDate}
                        onChange={handleChange}
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full px-4 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm md:text-base"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Additional Message or Notes
                      </label>
                      <div className="relative">
                        <MessageSquare className="absolute left-3 top-3 text-gray-400 h-4 w-4 md:h-5 md:w-5" />
                        <textarea
                          name="message"
                          rows={3}
                          value={formData.message}
                          onChange={handleChange}
                          className="w-full pl-8 md:pl-10 pr-4 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm md:text-base"
                          placeholder="Please describe your symptoms or reason for visit..."
                        />
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-blue-600 text-white py-3 md:py-4 px-6 rounded-lg hover:bg-blue-700 transition-colors font-medium text-base md:text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? 'Booking Appointment...' : 'Book Appointment'}
                    </button>
                  </form>
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-4 md:space-y-6">
                {/* Contact Info */}
                <div className="bg-white rounded-lg shadow-lg p-4 md:p-6">
                  <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-4">Contact Information</h3>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <Phone className="h-4 w-4 md:h-5 md:w-5 text-blue-600 flex-shrink-0" />
                      <div>
                        <div className="font-medium text-sm md:text-base">Phone</div>
                        <div className="text-sm text-gray-600">+255 757 685 894</div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Mail className="h-4 w-4 md:h-5 md:w-5 text-blue-600 flex-shrink-0" />
                      <div>
                        <div className="font-medium text-sm md:text-base">Email</div>
                        <div className="text-sm text-gray-600">info@rabininsiamemorial.co.tz</div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Clock className="h-4 w-4 md:h-5 md:w-5 text-blue-600 flex-shrink-0" />
                      <div>
                        <div className="font-medium text-sm md:text-base">Hours</div>
                        <div className="text-sm text-gray-600">Mon-Fri: 8:00 AM - 6:00 PM</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Important Notes */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 md:p-6">
                  <h3 className="text-base md:text-lg font-bold text-blue-900 mb-4">Important Notes</h3>
                  <ul className="space-y-2 text-sm text-blue-800">
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="h-3 w-3 md:h-4 md:w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span>Arrive 15 minutes before your appointment</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="h-3 w-3 md:h-4 md:w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span>Bring your ID and insurance card</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="h-3 w-3 md:h-4 md:w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span>List of current medications</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="h-3 w-3 md:h-4 md:w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span>Previous medical records if available</span>
                    </li>
                  </ul>
                </div>

                {/* Emergency Contact */}
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 md:p-6">
                  <h3 className="text-base md:text-lg font-bold text-red-900 mb-2">Medical Emergency?</h3>
                  <p className="text-sm text-red-800 mb-3">
                    For urgent medical situations, please call our emergency line or visit our emergency department immediately.
                  </p>
                  <div className="text-base md:text-lg font-bold text-red-600">+255 658 849 670</div>
                  <div className="text-sm text-red-600">24/7 Emergency Hotline</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Appointment;